// This is a global component used for handling cards and its actions(click) across the entire system.
// There are 3 types of scenarios handled in this card component,
// on First three screens, the cards shown are from user-category API, i.e. all categories, all brands and all models
// on Forth screen, an API is loaded and the above mentioned 3 types of scenarios are given below,
// 1. Data(cards) shown, comes from RepairDesk database
// 2. Data(cards) shown, comes from Our Own system database
// 3. Data(cards) shown, comes from Others case flow

import AppContext from "Context/appContext";
import axios from "axios";
import UrlServices from "../../services/Url";
import "components/Card/index.css";
import useTooltip from "components/Tooltip/useTooltip";
import Tooltip from "components/Tooltip/index";
import { useContext, useState, useEffect, useRef } from "react";

const Card = (props) => {
  const api_base_url = process.env.REACT_APP_APPOINTMENT_PRO_API_URL;
  
  const {
    index,
    product,
    filteredProducts,
    Icon,
    setSearch,
    setProducts
  } = props;

  const {
    currentState,
    setLoading,
    setProductCartData,
    progressState,
    setProgressState,
    productCartDetail,
    setProductCartDetail,
    encryptionKey,
    setFormData,
    formdata,
    notificationWay,
    setNotificationWay,
    reactHookFormData,
    isWebView,
    othersWorkflow,
    setOthersWorkflow,
    setIsBillable,
    countryDetails,
    userSettings
  } = useContext(AppContext);

  const {
    wasTruncated,
    truncatedText,
    showTooltip,
    setShowTooltip,
    tooltipPosition,
    setTooltipPosition
  } = useTooltip(product, isWebView);

  const [imageLoaded, setImageLoaded] = useState(true);
  const cardRef = useRef(null);

  // Hide tooltip when touch starts outside this card
  useEffect(() => {
    const handleDocumentTouchStart = (e) => {
      // Check if the touch started outside this card
      if (cardRef.current && !cardRef.current.contains(e.target)) {
        setShowTooltip(false);
      }
    };

    // Add event listener to document
    document.addEventListener('touchstart', handleDocumentTouchStart, { passive: true });

    return () => {
      // Cleanup: remove event listener
      document.removeEventListener('touchstart', handleDocumentTouchStart);
    };
  }, [setShowTooltip]);

  const getDataFromRD = async () => {
    if (product.billable) {
      setProgressState(3);
      setIsBillable(true)
    }
    
    setLoading(true);
    try {
      const { data, status } = await axios.post(
        `${api_base_url}/${UrlServices.rdDeviceDetail}`,
        {
          bzy_d: product.billable ? "" : product.rd_device_id,
          bzy_b: product.billable ? true : "",
          bzy_c: product?.rd_category_id,
          q_: encryptionKey,
        }
      );
      if (status === 200) {
        setProductCartDetail({ ...productCartDetail, services: [], totalPrice: 0 });
        const item = data.data[0];
        const price = item?.sale_price?.startsWith("0")
          ? item?.retail_price
          : item?.sale_price;
        // setProducts(data?.data?.length > 0 ? data : allServicesForSubId);

        // in case, if there are NO AVAILABLE SERVICES FOUND, so go with "Others" workflow
        if (data.data.length === 0) {
          // others case flow
          goWithOthersFlow("Others");
          setLoading(false);
          setOthersWorkflow(true);
          setFormData({ ...formdata, model: product.name });
          return;
        }

        setProducts(data?.data);
        setProductCartData({
          device_name: product.name,
          rd_device_id: product.billable ? "" : product.rd_device_id,
          price: price,
        });
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const getModelServices = async () => {
    setLoading(true);
    try {
      const { data, status } = await axios.post(
        `${api_base_url}/${UrlServices.modelServices}`,
        {
          Id: product._id,
          q_: encryptionKey,
        }
      );
      if (status === 200) {
        // setProducts(data.length > 0 ? data : allServicesForSubId);
        
        // in case, if there are NO AVAILABLE SERVICES FOUND, so go with "Others" workflow
        if (data.length === 0) {
          // others case flow
          goWithOthersFlow("Others");
          setLoading(false);
          setOthersWorkflow(true);
          setFormData({ ...formdata, model: product.name });
          return;
        }
        
        setProducts(data);
        setFormData({ ...formdata, model: product.name });
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  // Tooltip for Desktop (Mouse Hover)
  const handleDesktopMouseEnter = (e) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const topOffset = 50; // Use desktop offset
    setTooltipPosition({ top: rect.top + topOffset, left: rect.left + rect.width / 2 });
    setShowTooltip(true); // Show tooltip on hover
  };

  // Tooltip for Mobile (Touch)
  const handleMobileTouchStart = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const topOffset = 35; // Use mobile offset (smaller)
    setTooltipPosition({ top: rect.top + topOffset, left: rect.left + rect.width / 2 });
    setShowTooltip(true); // Show tooltip on touch
  };

  // Hide tooltip when touch ends or is cancelled
  const handleMobileTouchEnd = () => {
    setShowTooltip(false);
  };

  // Hide tooltip when touch is cancelled (e.g., user scrolls or moves finger away)
  const handleMobileTouchCancel = () => {
    setShowTooltip(false);
  };

  // Hide tooltip when finger moves away from the element
  const handleMobileTouchMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const touch = e.touches[0] || e.changedTouches[0];
    
    // Check if touch is still within the element bounds
    if (touch) {
      const isWithinBounds = 
        touch.clientX >= rect.left &&
        touch.clientX <= rect.right &&
        touch.clientY >= rect.top &&
        touch.clientY <= rect.bottom;
      
      if (!isWithinBounds) {
        setShowTooltip(false);
      }
    }
  };

  const updateValue = () => {
    currentState === "PRODUCT" && progressState !== 3 && setSearch("");
    if (index !== undefined) filteredProducts(index, product?.name, product?._id);

    if (progressState === 4) {
      setNotificationWay(product?.value);
    }

    // 3 Gates Type Scenarios for Card Click Event,
    // As this is a global component used for handling card and its actions across the entire system.
    if (productCartDetail?.skipForOthers && !othersWorkflow) {
      // others case flow
      goWithOthersFlow();
    } else if ((product.brand && product.isRD) || (product.billable && product.isRD && !product.children?.length && progressState !== 3) || (product.billable && product.isRD && product.children?.length && product?.category?.billable)) {
      // repairdesk database case flow
      getDataFromRD();
    } else if (product.brand && !product?.isRD && !product.model) {
      // our database case flow
      getModelServices();
    }
  };

  const goWithOthersFlow = (key) => {
    // others case flow
    product.active = !product.active;
    filteredProducts(index, key ? key : product?.name, product?._id);
    if (progressState === 3) {
      setProductCartData(null);
      setFormData({ ...formdata, model: "" });
      reactHookFormData.setValue("model", "");
    }
  }

  const handleImageError = (e) => {
    e.target.style.display = 'none'; // Hide the image when it fails to load
    setImageLoaded(false);
  };

  return (
    (product._id !== "others" || progressState !== 3) && (
      <div
        ref={cardRef}
        onClick={updateValue}
        onMouseEnter={handleDesktopMouseEnter}
        onMouseLeave={() => setShowTooltip(false)}
        onTouchStart={handleMobileTouchStart}
        onTouchEnd={handleMobileTouchEnd}
        onTouchCancel={handleMobileTouchCancel}
        onTouchMove={handleMobileTouchMove}
        style={{
          '--border-bg-color': userSettings?.colourHex?.buttonAndBorderColor || '#ebebeb'
        }}
        className={`rd-quote-cursor-pointer quote-product-card quote-product-card-width-mob rd-quote-pt-[0px] rd-quote-tooltip
          ${progressState === 3 && product.active ? "rd-quote-border-2 rd-quote-rounded-md rd-quote-border-[var(--border-bg-color)]" : ""}
          ${progressState === 4 && product?.value === 'sms' && 
            (countryDetails?.country !== 'Australia' && 
              countryDetails?.country !== 'United States' && 
              countryDetails?.country !== 'Canada' && 
              countryDetails?.country !== 'United Kingdom') 
              ? 'rd-quote-pointer-events-none rd-quote-opacity-50' 
              : ''
          }
          ${
            product?.model?.name
              ? "rd-quote-rounded-md"
              : product?.value === "sms" ||
                product?.value === "email" ||
                product?.value === "phone"
              ? "xs:rd-quote-h-[98px] lg:rd-quote-h-[112px] rd-quote-rounded-md lg:hover:rd-quote-border-2 lg:hover:rd-quote-border-[var(--border-bg-color)]"
              : progressState === 1 || progressState === 2
              ? "rd-quote-h-[112px] xs:rd-quote-w-[48%] lg:rd-quote-w-[183.6px] rd-quote-rounded-md lg:hover:rd-quote-border-2 lg:hover:rd-quote-border-[var(--border-bg-color)]"
              : progressState === 3
              ? "rd-quote-h-[102px] xs:rd-quote-w-[48%] lg:rd-quote-w-[160px] rd-quote-rounded-md lg:hover:rd-quote-border-2 lg:hover:rd-quote-border-[var(--border-bg-color)]"
              : "quote-card-width-mobile rd-quote-h-[166px] rd-quote-w-[242px] -rd-quote-mb-[10px] hover:rd-quote-border-2 hover:rd-quote-border-[var(--border-bg-color)]"
          }
          ${product?.value === notificationWay && progressState === 4 ? "rd-quote-border-2 rd-quote-border-[var(--border-bg-color)]" : ""}
        `}
      >
        {/* Tooltip */}
        {showTooltip && wasTruncated && (
          <Tooltip productName={product?.name} position={tooltipPosition} />
        )}
  
        <button type="submit" className="rd-quote-bg-transparent quote-product-card rd-quote-max-w-full rd-quote-gap-y-[3px] rd-quote-border-0 rd-quote-border-none">
          {!Icon && product.picture && (
            <img
              className={`${
                product?.model?.name || product?.name ? "" : "rd-quote-h-[140px]"
              }
              ${
                progressState === 1 || progressState === 2
                  ? "quote-product-card-img-1-2"
                  : progressState === 3
                  ? "quote-product-card-img-3"
                  : "quote-product-card-img"
              }`}
              alt="P"
              src={product.picture}
              onError={handleImageError}
            />
          )}
          {(product.image && !product.picture && progressState === 3) && (
            <i className={`ti ti-${product.image} quote-product-card-img-3 rd-quote-text-3xl rd-quote-font-extralight rd-quote-text-gray-500`}></i>
          )}
          {/* default icons are 'tool' now */}
          {(!product.image && !product.picture && progressState === 3) && (
            <i className={`ti ti-tool quote-product-card-img-3 rd-quote-text-3xl rd-quote-font-extralight rd-quote-text-gray-500`}></i>
          )}
          <span className="rd-quote-text-gray-500"> {Icon} </span>
          <p
            className={`quote-text3 rd-quote-text-center rd-quote-mb-0
            ${(!product.picture || !product.image) ? "" : "rd-quote-max-w-[148px]"}
            ${(!product.picture || !product.image) && progressState !== 4 ? "rd-quote-text-base !rd-quote-leading-[1.3rem]" : ""}
            ${!isWebView && imageLoaded && (progressState === 0 || progressState === 1 || progressState === 2 || progressState === 3) ? "!rd-quote-text-[0.78rem] quote-font-text-size" : ""}
            `}
          >
            {truncatedText}
          </p>
        </button>
      </div>
    )
  );
  
};

export default Card;